import React, { useState } from 'react';
import PropTypes from 'prop-types';

import ImageCropped from 'views_shared/app/javascript/components/ImageCropped';
import { normalizeProfileType, isMediaType } from 'views_shared/app/javascript/utils/profileTypeHelpers';
import { profileUrl } from 'views_shared/app/javascript/utils/urls';

const BIO_CHAR_LIMIT = 280;
const HASHTAG_DEFAULT_LIMIT = 5;

// `viewProfileUrl` overrides the default profileUrl for non-profile entities
// promoted into the participant list — e.g. an origin Product or Expert needs
// productUrl/expertUrl, not profileUrl (which would route products to /sources).
function ProfileCard({
 profile, bio = '', headline = '', hashtags = [], viewProfileUrl = profileUrl,
}) {
  const [expandedBio, setExpandedBio] = useState(false);
  const [expandedHashtags, setExpandedHashtags] = useState(false);

  const displayHeadline = headline || profile.title_at_workplace;
  const shouldTruncateBio = bio && bio.length > BIO_CHAR_LIMIT;
  const displayBio = shouldTruncateBio && !expandedBio ? `${bio.substring(0, BIO_CHAR_LIMIT)}...` : bio;
  const shouldTruncateHashtags = hashtags.length > HASHTAG_DEFAULT_LIMIT;
  const displayedHashtags = expandedHashtags ? hashtags : hashtags.slice(0, HASHTAG_DEFAULT_LIMIT);
  const isMedia = isMediaType(normalizeProfileType(profile.type_of_profile));

  return (
    <>
      {/* Profile Card */}
      <div className="d-flex flex-column align-items-center p-3">
        <ImageCropped
          roundedCircle
          src={profile.profile_image_url}
          alt={profile.name}
          size={56}
          wrapperClasses="mb-3"
          fallbackToInitials
        />
        <div className="text-center w-100">
          <p className="paragraph-md fw-bold mb-0">{profile.name}</p>
          {displayHeadline && <p className="paragraph-sm text-secondary mt-1 mb-0">{displayHeadline}</p>}
        </div>
        <a
          href={viewProfileUrl(profile)}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-secondary btn-sm w-100 mt-3"
        >
          View Profile
        </a>
      </div>

      {/* About Section */}
      {bio && (
        <div className="mt-2 mb-2 px-3">
          <p className="fw-bold paragraph-sm mb-1">About {profile.first_name || profile.name}:</p>
          <p className="paragraph-sm mb-0">
            {displayBio}
            {shouldTruncateBio && !expandedBio && (
              <>
                {' '}
                <button
                  type="button"
                  className="btn btn-link p-0 text-primary border-0"
                  style={{ font: 'inherit', marginBottom: '2px' }}
                  onClick={() => setExpandedBio(true)}
                >
                  read more
                </button>
              </>
            )}
          </p>
        </div>
      )}

      {/* Hashtags Section */}
      {hashtags.length > 0 && (
        <div className="p-3 border-top">
          <p className="fw-bold paragraph-sm mb-2">{isMedia ? 'Covers:' : 'Expert on:'}</p>
          <div className="d-flex flex-wrap gap-2">
            {displayedHashtags.map((tag) => {
              const tagName = tag.hashtag_name || '';
              const displayTag = tagName.startsWith('#') ? tagName : `#${tagName}`;
              return (
                <span
                  key={tag.hashtag_id || tagName}
                  className="sidebar-hashtag-badge badge fw-bold paragraph-xs rounded-pill d-inline-flex align-items-center text-accent-blue-80 bg-accent-blue-10 border border-accent-blue-30"
                >
                  {displayTag}
                </span>
              );
            })}
          </div>
          {shouldTruncateHashtags && (
            <button
              type="button"
              className="p-0 mt-2 ms-2 d-inline-block paragraph-xs text-primary-60 border-0 bg-transparent"
              style={{ lineHeight: 'inherit' }}
              onClick={() => setExpandedHashtags((prev) => !prev)}
            >
              {expandedHashtags ? 'Show Less' : `Show ${hashtags.length - HASHTAG_DEFAULT_LIMIT} more`}
            </button>
          )}
        </div>
      )}
    </>
  );
}

ProfileCard.propTypes = {
  profile: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    first_name: PropTypes.string,
    profile_image_url: PropTypes.string,
    title_at_workplace: PropTypes.string,
    type_of_profile: PropTypes.string,
  }).isRequired,
  bio: PropTypes.string,
  headline: PropTypes.string,
  hashtags: PropTypes.arrayOf(PropTypes.shape({ hashtag_id: PropTypes.number, hashtag_name: PropTypes.string })),
  viewProfileUrl: PropTypes.func,
};

export default ProfileCard;
